import React, { useEffect, useState, useMemo, memo, useCallback } from "react";
import "./css/Events.css";
import { Button } from "../../../../../ui-kit-v2/src/button/button";
import DataTable from "./DataTable";

import { NavOptions, GPEventResponse, GPGamePlayerResponse, VideoSidebar } from "../../types";

import { getPlayerDetails, formatTime, getEventFilterValuesByCategory, getEventDetails } from "./functions";
import { PlayerStatType } from "../PlayerStats/definition";
import { useCategoryTab, useNavOptions } from "./contexts/DataInsightContext";
import { EventFilterCondition } from "common/src/gamePage/types";
import { getTextColorForBackground } from "../../functions";
import { useTeamInfo } from "./contexts/DataInsightContext";
import { _ } from "i18n";
import { Link, useParams } from "react-router";
import { GAME_FILTERED_STATE_KEY } from "./constants";
import { SelectOption } from "./ParameterOptions";
import { Play } from "lucide-react";
import { useGameStore } from "../../store";

interface EventsProps {
	events: GPEventResponse[];
	activeEvent: GPEventResponse | null;
	players: Record<number, GPGamePlayerResponse>;
	filters: EventFilterCondition[];
	eventType: string;
	parameterFilters?: Record<string, SelectOption>;
	onRowClick?: (event: GPEventResponse) => void;
}

// Extract and memoize row components to prevent unnecessary re-renders
interface PlayerDetailsRowProps {
	event: GPEventResponse;
	players: Record<number, GPGamePlayerResponse>;
	shirtColor: string | null;
}

const PlayerDetailsRow = memo(
	({ event, players, shirtColor }: PlayerDetailsRowProps) => {
		const playerDetails = getPlayerDetails(event, players);
		return (
			<div className="player-details">
				<div
					className="player-details__number"
					style={{
						backgroundColor: shirtColor || "#666b88",
						color: getTextColorForBackground(shirtColor || "rgba(240, 240, 240, 0.8)"),
					}}
				>
					{playerDetails?.numberSign}
				</div>
				<div className="player-details__info">
					<div className="player-details__info-name">
						<Link to={playerDetails?.playerUrl as string} target="_blank" className="player-details__info-name-link">
							{playerDetails?.name}
						</Link>
					</div>
					<div className="player-details__profile">
						<span className="player-details__profile-item">{playerDetails?.positionKey}</span>
					</div>
				</div>
			</div>
		);
	},
	(prevProps, nextProps) => {
		return prevProps.event.id === nextProps.event.id && prevProps.players === nextProps.players && prevProps.shirtColor === nextProps.shirtColor;
	}
);

PlayerDetailsRow.displayName = "PlayerDetailsRow";

interface EventDetailsRowProps {
	event: GPEventResponse;
}

const EventDetailsRow = memo(({ event }: EventDetailsRowProps) => {
	return (
		<div className="event-details">
			{getEventDetails(event).map((detail) => (
				<div key={detail} className="event-details__detail">
					{_(detail as any)}
				</div>
			))}
		</div>
	);
});

EventDetailsRow.displayName = "EventDetailsRow";

const EventTimeRow = memo(({ event }: EventDetailsRowProps) => {
	return (
		<div className="event-time">
			<div className="event-time__time">{formatTime(event.footballTime)}</div>
		</div>
	);
});

EventTimeRow.displayName = "EventTimeRow";

interface ViewEventButtonRowProps {
	event: GPEventResponse;
	handleChangeNavOptions: (option: NavOptions) => void;
	setSidebar: (sidebar: VideoSidebar) => void;
}

const ViewEventButtonRow = memo(({ event, handleChangeNavOptions, setSidebar }: ViewEventButtonRowProps) => {
	const handleViewEvent = useCallback(() => {
		const filterValuesData = [{ key: "event.id", value: [event.id.toString()] }];

		localStorage.setItem(
			GAME_FILTERED_STATE_KEY,
			JSON.stringify({
				filterValuesData,
				eventTime: event.clipTime,
			})
		);
		handleChangeNavOptions(NavOptions.FullGame);
	}, [event, handleChangeNavOptions]);

	return (
		<Button className="btn btn btn--grey view-event-button w-max" variant="secondary" onClick={handleViewEvent}>
			<Play size={14} />
		</Button>
	);
});

ViewEventButtonRow.displayName = "ViewEventButtonRow";

const Events = ({ events, activeEvent, players, filters, eventType, parameterFilters, onRowClick }: EventsProps) => {
	const { handleChangeNavOptions } = useNavOptions();
	const { teamInfo } = useTeamInfo();
	const [shirtColor, setShirtColor] = useState<string | null>("rgba(240, 240, 240, 0.8)");

	useEffect(() => {
		if (teamInfo && filters.length > 0) {
			const teamId = filters.find((filter) => filter.type === "team")?.value[0];
			if (teamId === teamInfo.homeTeam.id.toString()) {
				setShirtColor(teamInfo.homeTeam.shirtColor);
			} else {
				setShirtColor(teamInfo.awayTeam.shirtColor);
			}
		}
	}, [teamInfo, filters]);

	const { setSidebar } = useGameStore();

	const onPressPlayAll = useCallback(() => {
		if (!events?.length) {
			return;
		}

		const filterValuesData = getEventFilterValuesByCategory(events[0].category, eventType as PlayerStatType, filters, teamInfo, parameterFilters);

		localStorage.setItem(
			GAME_FILTERED_STATE_KEY,
			JSON.stringify({
				filterValuesData,
			})
		);

		setSidebar(VideoSidebar.Filters);
		handleChangeNavOptions(NavOptions.FullGame);
	}, [events, eventType, filters, teamInfo, parameterFilters, setSidebar, handleChangeNavOptions]);

	const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);

	// Memoize raw values computation
	const eventRawValues = useMemo(() => {
		const rawValues: any[][] = [];
		events.forEach((event) => {
			const playerName = getPlayerDetails(event, players)?.name || "";
			const eventDetails = getEventDetails(event).join(", ") || "";
			const eventTime = event.clipTime || 0;
			const buttonLabel = "View";
			rawValues.push([playerName, eventDetails, eventTime, buttonLabel]);
		});
		return rawValues;
	}, [events, players]);

	// Memoize rendered values using extracted memoized components
	// This prevents re-rendering when the row components haven't changed
	const eventRenderedValues = useMemo(() => {
		const renderedValues: any[][] = [];
		events.forEach((event, index) => {
			renderedValues.push([
				<PlayerDetailsRow key={`player-${event.id}`} event={event} players={players} shirtColor={shirtColor} />,
				<EventDetailsRow key={`details-${event.id}`} event={event} />,
				<EventTimeRow key={`time-${event.id}`} event={event} />,
				<ViewEventButtonRow
					key={`button-${event.id}`}
					event={event}
					handleChangeNavOptions={handleChangeNavOptions}
					setSidebar={setSidebar}
				/>,
			]);
		});
		return renderedValues;
	}, [events, players, shirtColor, handleChangeNavOptions, setSidebar]);

	useEffect(() => {
		if (activeEvent) {
			const index = events.findIndex((event) => event.id === activeEvent.id);
			setActiveRowIndex(index >= 0 ? index : null);
		} else {
			setActiveRowIndex(null);
		}
	}, [activeEvent, events]);

	const headers = useMemo(
		() => [
			_("gamePage.dataInsights.label.player"),
			_("gamePage.dataInsights.label.details"),
			_("gamePage.dataInsights.label.time"),
			_("gamePage.dataInsights.label.viewEvent"),
		],
		[]
	);

	return (
		<>
			<div className="events-container__header">
				<h3>{_("label.events")}</h3>
				<Button className="btn btn-light-blue btn-save-view w-max" onClick={onPressPlayAll} size="small" iconLeft={<Play size={14} />}>
					<span>{_("gamePage.dataInsights.label.playAll")}</span>
				</Button>
			</div>
			<div className="events-container__body">
				<DataTable
					headers={headers}
					rawValues={eventRawValues}
					renderedValues={eventRenderedValues}
					activeRow={activeRowIndex}
					sortableColumns={[true, false, true, false]}
					onRowClick={(rowIndex) => {
						if (onRowClick && events[rowIndex]) {
							onRowClick(events[rowIndex]);
						}
					}}
				/>
			</div>
		</>
	);
};

export default Events;
